import React, { useMemo, useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { ICS204_TABLE } from '../AssignmentList/assignmentCardStyles';

/** A row's values while it is being typed, keyed by the column's entity field. */
export type MedicalPlanDraft = Record<string, string | boolean>;

/**
 * One printed column of a 206 table.
 *
 * `radioPair` covers Level of Service, where the printed form gives a row one
 * ALS/BLS pair: two booleans on the record, but only ever one of them true.
 */
export type MedicalPlanColumn =
  | {
      kind: 'text';
      field: string;
      header: string;
      placeholder?: string;
      maxLength?: number;
    }
  | { kind: 'checkbox'; field: string; header: string }
  | {
      kind: 'radioPair';
      header: string;
      options: { field: string; label: string }[];
    };

export type MedicalPlanTableProps<TEntity extends { id: string }> = {
  /** Names the table for tests and for the searchbox. */
  testId: string;
  entityNoun: string;
  entityNounPlural: string;
  addButtonLabel: string;
  noDataMessage: string;
  entities: TEntity[];
  columns: MedicalPlanColumn[];
  emptyDraft: MedicalPlanDraft;
  /** Column that has to be filled before the row can be saved. */
  requiredField: string;
  /** Column whose value names the row in button labels. */
  rowLabelField: string;
  isSaving?: boolean;
  onCreate: (draft: MedicalPlanDraft) => Promise<void>;
  onUpdate: (entity: TEntity, draft: MedicalPlanDraft) => Promise<void>;
  onDelete: (entity: TEntity) => Promise<void>;
  readOnly?: boolean;
};

const columnKey = (column: MedicalPlanColumn) =>
  column.kind === 'radioPair' ? column.header : column.field;

/** Every entity field a column set touches, for reading a row into a draft. */
const draftFromEntity = (
  columns: MedicalPlanColumn[],
  entity: Record<string, unknown>,
  emptyDraft: MedicalPlanDraft
): MedicalPlanDraft => {
  const draft: MedicalPlanDraft = { ...emptyDraft };
  for (const column of columns) {
    const fields =
      column.kind === 'radioPair'
        ? column.options.map((option) => option.field)
        : [column.field];
    for (const field of fields) {
      const value = entity[field];
      draft[field] =
        column.kind === 'text' ? ((value as string) ?? '') : !!value;
    }
  }
  return draft;
};

export function MedicalPlanTable<TEntity extends { id: string }>({
  testId,
  entityNoun,
  entityNounPlural,
  addButtonLabel,
  noDataMessage,
  entities,
  columns,
  emptyDraft,
  requiredField,
  rowLabelField,
  isSaving = false,
  onCreate,
  onUpdate,
  onDelete,
  readOnly = false,
}: MedicalPlanTableProps<TEntity>) {
  const [isAdding, setIsAdding] = useState(false);
  const [newRow, setNewRow] = useState<MedicalPlanDraft>({ ...emptyDraft });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<MedicalPlanDraft | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const textFields = useMemo(
    () =>
      columns
        .filter((column) => column.kind === 'text')
        .map((column) => column.field),
    [columns]
  );

  const filteredEntities = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return entities;
    return entities.filter((entity) =>
      textFields.some((field) => {
        const value = (entity as Record<string, unknown>)[field];
        return typeof value === 'string' && value.toLowerCase().includes(term);
      })
    );
  }, [entities, searchTerm, textFields]);

  const startAddRow = () => {
    setNewRow({ ...emptyDraft });
    setIsAdding(true);
  };

  const cancelAddRow = () => {
    setIsAdding(false);
    setNewRow({ ...emptyDraft });
  };

  const handleCreate = async () => {
    await onCreate(newRow);
    cancelAddRow();
  };

  const startEditRow = (entity: TEntity) => {
    setEditingId(entity.id);
    setEditDraft(
      draftFromEntity(
        columns,
        entity as unknown as Record<string, unknown>,
        emptyDraft
      )
    );
  };

  const cancelEditRow = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const handleSaveEdit = async (entity: TEntity) => {
    if (!editDraft) return;
    await onUpdate(entity, editDraft);
    cancelEditRow();
  };

  const rowLabelFor = (entity: TEntity) => {
    const value = (entity as Record<string, unknown>)[rowLabelField];
    return typeof value === 'string' && value.trim()
      ? value
      : `this ${entityNoun.toLowerCase()}`;
  };

  const columnCount = columns.length + (readOnly ? 0 : 1);

  const renderEditableCell = (
    column: MedicalPlanColumn,
    draft: MedicalPlanDraft,
    onChange: (next: MedicalPlanDraft) => void,
    rowLabel: string
  ): React.ReactNode => {
    if (column.kind === 'text') {
      return (
        <Input
          className="tw:min-w-32"
          aria-label={`${column.header} for ${rowLabel}`}
          placeholder={column.placeholder}
          maxLength={column.maxLength}
          value={(draft[column.field] as string) ?? ''}
          onChange={(event) =>
            onChange({ ...draft, [column.field]: event.target.value })
          }
        />
      );
    }

    if (column.kind === 'checkbox') {
      return (
        <Checkbox
          aria-label={`${column.header} for ${rowLabel}`}
          checked={!!draft[column.field]}
          onCheckedChange={(checked) =>
            onChange({ ...draft, [column.field]: !!checked })
          }
        />
      );
    }

    const selected = column.options.find((option) => draft[option.field]);
    return (
      <RadioGroup
        aria-label={`${column.header} for ${rowLabel}`}
        className="tw:flex tw:gap-3"
        value={selected?.field ?? ''}
        onValueChange={(next) =>
          onChange({
            ...draft,
            ...Object.fromEntries(
              column.options.map((option) => [
                option.field,
                option.field === next,
              ])
            ),
          })
        }
      >
        {column.options.map((option) => (
          <label
            key={option.field}
            className="tw:flex tw:items-center tw:gap-2 tw:text-sm"
          >
            <RadioGroupItem value={option.field} />
            {option.label}
          </label>
        ))}
      </RadioGroup>
    );
  };

  const renderReadCell = (
    column: MedicalPlanColumn,
    entity: TEntity
  ): React.ReactNode => {
    const record = entity as Record<string, unknown>;
    if (column.kind === 'text') {
      return (record[column.field] as string) || '—';
    }
    if (column.kind === 'checkbox') {
      return record[column.field] ? 'Yes' : 'No';
    }
    const selected = column.options.find((option) => record[option.field]);
    return selected?.label ?? '—';
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Input
          className="tw:max-w-xs"
          aria-label={`Search ${entityNounPlural.toLowerCase()}`}
          placeholder={`Search ${entityNounPlural.toLowerCase()}...`}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            className="tw:ml-auto"
            disabled={isAdding}
            onClick={startAddRow}
          >
            <Plus className="tw:size-4" />
            {addButtonLabel}
          </Button>
        )}
      </div>

      <div className="tw:overflow-x-auto">
        <Table className={ICS204_TABLE} data-testid={testId}>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={columnKey(column)}>{column.header}</TableHead>
              ))}
              {!readOnly && <TableHead className="tw:w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!readOnly && isAdding && (
              <TableRow data-testid={`${testId}-new-row`}>
                {columns.map((column) => (
                  <TableCell key={columnKey(column)}>
                    {renderEditableCell(
                      column,
                      newRow,
                      setNewRow,
                      `the new ${entityNoun.toLowerCase()}`
                    )}
                  </TableCell>
                ))}
                <TableCell>
                  <div className="tw:flex tw:justify-end tw:gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Cancel the new ${entityNoun.toLowerCase()}`}
                      onClick={cancelAddRow}
                    >
                      <X className="tw:size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Save the new ${entityNoun.toLowerCase()}`}
                      disabled={
                        isSaving ||
                        !String(newRow[requiredField] ?? '')
                          .toString()
                          .trim()
                      }
                      onClick={() => void handleCreate()}
                    >
                      <Check className="tw:size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}

            {filteredEntities.length === 0 && !isAdding ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="tw:text-center">
                  {searchTerm ? 'No results!' : noDataMessage}
                </TableCell>
              </TableRow>
            ) : (
              filteredEntities.map((entity) => {
                const isEditing = editingId === entity.id && editDraft != null;
                const rowLabel = rowLabelFor(entity);

                return (
                  <TableRow key={entity.id}>
                    {columns.map((column) => (
                      <TableCell key={columnKey(column)}>
                        {isEditing
                          ? renderEditableCell(
                              column,
                              editDraft,
                              setEditDraft,
                              rowLabel
                            )
                          : renderReadCell(column, entity)}
                      </TableCell>
                    ))}
                    {!readOnly && (
                      <TableCell>
                        <div className="tw:flex tw:justify-end tw:gap-1">
                          {isEditing ? (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Cancel editing ${rowLabel}`}
                                onClick={cancelEditRow}
                              >
                                <X className="tw:size-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Save ${rowLabel}`}
                                disabled={isSaving}
                                onClick={() => void handleSaveEdit(entity)}
                              >
                                <Check className="tw:size-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Edit ${rowLabel}`}
                                onClick={() => startEditRow(entity)}
                              >
                                <Pencil className="tw:size-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Delete ${rowLabel}`}
                                onClick={() => void onDelete(entity)}
                              >
                                <Trash2 className="tw:size-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <span className="tw:text-sm tw:text-muted-foreground">
        {filteredEntities.length}{' '}
        {filteredEntities.length === 1 ? entityNoun : entityNounPlural}
      </span>
    </div>
  );
}
